import { supabase } from '../supabaseClient';
import {
  FORFEO_RESERVE_VIRTUAL_TOUR_SCHEMA,
  validateVirtualTourCommand,
  type ForfeoReserveVirtualTourCommand,
  type ForfeoReserveVirtualTourMediaKind,
  type ForfeoReserveVirtualTourSnapshot,
} from '../../supabase/functions/_shared/forfeo-reserve-virtual-tour';

export const FORFEO_RESERVE_VIRTUAL_TOUR_FEATURE_FLAG =
  'VITE_FORFEO_RESERVE_VIRTUAL_TOUR_V1' as const;

const BUCKET = 'reserve-virtual-tours';

export function isForfeoReserveVirtualTourEnabled(): boolean {
  return import.meta.env.VITE_FORFEO_RESERVE_VIRTUAL_TOUR_V1 === 'true';
}

export class ForfeoReserveVirtualTourClientError extends Error {
  readonly code: string;

  constructor(code: string) {
    super(code);
    this.name = 'ForfeoReserveVirtualTourClientError';
    this.code = code;
  }
}

function correlationId(): string {
  return `reserve:virtual-tour:${crypto.randomUUID()}`;
}

function idempotencyKey(kind: string): string {
  return `reserve:virtual-tour:${kind}:${crypto.randomUUID()}`;
}

async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(
    'reserve-virtual-tours',
    { body },
  );
  if (error) {
    throw new ForfeoReserveVirtualTourClientError(
      String((data as { error?: string } | null)?.error || 'network_error'),
    );
  }
  if (!data?.ok) {
    throw new ForfeoReserveVirtualTourClientError(
      String(data?.error || 'virtual_tour_operation_failed'),
    );
  }
  return data as T;
}

export async function fetchPublicVirtualTour(
  businessId: string,
  locale: 'fr-CA' | 'en-CA' = 'fr-CA',
): Promise<ForfeoReserveVirtualTourSnapshot | null> {
  const data = await invoke<{
    ok: true;
    schema: typeof FORFEO_RESERVE_VIRTUAL_TOUR_SCHEMA;
    snapshot: ForfeoReserveVirtualTourSnapshot | null;
  }>({
    action: 'public_snapshot',
    request: {
      businessId,
      locale,
      correlationId: correlationId(),
    },
  });
  return data.snapshot;
}

export async function fetchMerchantVirtualTours(
  businessId: string,
): Promise<ForfeoReserveVirtualTourSnapshot[]> {
  const data = await invoke<{
    ok: true;
    tours: ForfeoReserveVirtualTourSnapshot[];
  }>({
    action: 'merchant_snapshot',
    request: {
      businessId,
      correlationId: correlationId(),
    },
  });
  return data.tours;
}

export function createVirtualTourCommand(
  input: Omit<ForfeoReserveVirtualTourCommand, 'idempotencyKey' | 'correlationId'>
    & Partial<Pick<ForfeoReserveVirtualTourCommand, 'idempotencyKey' | 'correlationId'>>,
): ForfeoReserveVirtualTourCommand {
  const command: ForfeoReserveVirtualTourCommand = {
    ...input,
    idempotencyKey: input.idempotencyKey || idempotencyKey(input.kind),
    correlationId: input.correlationId || correlationId(),
  };
  const errors = validateVirtualTourCommand(command);
  if (errors.length > 0) {
    throw new ForfeoReserveVirtualTourClientError(errors.join(','));
  }
  return command;
}

export async function applyVirtualTourCommand(
  command: ForfeoReserveVirtualTourCommand,
): Promise<Record<string, unknown>> {
  const errors = validateVirtualTourCommand(command);
  if (errors.length > 0) {
    throw new ForfeoReserveVirtualTourClientError(errors.join(','));
  }
  const data = await invoke<{ ok: true; result: Record<string, unknown> }>({
    action: 'command',
    command,
  });
  return data.result;
}

async function sha256(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

export async function uploadVirtualTourMedia(input: {
  businessId: string;
  tourId: string;
  file: File;
  mediaKind: ForfeoReserveVirtualTourMediaKind;
  altText: string;
}): Promise<{ mediaId: string }> {
  const checksum = await sha256(input.file);
  const prepared = await invoke<{
    ok: true;
    upload: {
      bucket: typeof BUCKET;
      path: string;
      token: string;
      uploadId: string;
    };
  }>({
    action: 'create_upload',
    request: {
      businessId: input.businessId,
      tourId: input.tourId,
      fileName: input.file.name,
      mimeType: input.file.type,
      bytes: input.file.size,
      mediaKind: input.mediaKind,
      altText: input.altText,
      checksumSha256: checksum,
      correlationId: correlationId(),
    },
  });

  const { error } = await supabase.storage
    .from(prepared.upload.bucket)
    .uploadToSignedUrl(
      prepared.upload.path,
      prepared.upload.token,
      input.file,
      { contentType: input.file.type },
    );
  if (error) {
    throw new ForfeoReserveVirtualTourClientError('signed_upload_failed');
  }

  const completed = await invoke<{
    ok: true;
    media: { id: string };
  }>({
    action: 'complete_upload',
    request: {
      businessId: input.businessId,
      tourId: input.tourId,
      uploadId: prepared.upload.uploadId,
      path: prepared.upload.path,
      checksumSha256: checksum,
      correlationId: correlationId(),
    },
  });

  return { mediaId: completed.media.id };
}
